import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import {
  CustomAppDialog,
  CustomDialogCloseButton,
} from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import {
  closePurchaseItemDeleteConfirmDialog,
  openPurchaseItemDeleteConfirmDialog,
  setPurchaseItemIndex,
} from "./purchaseSlice";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import { findAllActiveItem } from "../item/services/item.services";
import FormSelectionField from "../../components/formFields/FormSelectionField";

const PurchaseItemArray = (fieldArrayRenderProps: FieldArrayRenderProps) => {
  const dispatch = useAppDispatch();
  const PurchaseItemIndex = useAppSelector(
    (state) => state.purchase.PurchaseItemIndex,
  );
  const PurchaseItemDeleteConfirmDialog = useAppSelector(
    (state) => state.purchase.PurchaseItemDeleteConfirmDialog,
  );
  const fieldName = fieldArrayRenderProps.name;
  const ItemList = useAppSelector((state) => state.item.ItemList);

  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index]) {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closePurchaseItemDeleteConfirmDialog());
        dispatch(setPurchaseItemIndex(-1));
      }
    },
    [fieldArrayRenderProps, dispatch],
  );

  const pushElementInArray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        ItemID: null,
        Weight: null,
        StonePcs: null,
        CTWeight: null,
        Charges: null,
        CustomeWeight: null,
        ChargesForLable: null,
      },
    });
  }, [fieldArrayRenderProps]);

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(openPurchaseItemDeleteConfirmDialog());
    dispatch(setPurchaseItemIndex(index));
  };

  // Auto-calculate Charges based on Weight or CTWeight if needed
  const handleChargesCalculation = (index: number, fieldName: string) => {
    const weight =
      fieldArrayRenderProps.formRenderProps.valueGetter(
        `${fieldName}.${index}.Weight`,
      ) || 0;
    const ctWeight =
      fieldArrayRenderProps.formRenderProps.valueGetter(
        `${fieldName}.${index}.CTWeight`,
      ) || 0;
    const chargesPerUnit =
      fieldArrayRenderProps.formRenderProps.valueGetter(`RatePerGramCT`) || 0;

    // Example: Calculate charges based on weight or CT weight
    if (fieldName === "Weight" || fieldName === "CTWeight") {
      const calculatedCharges = (weight || ctWeight) * chargesPerUnit;
      fieldArrayRenderProps.formRenderProps.onChange(
        `${fieldName}.${index}.Charges`,
        { value: parseFloat(calculatedCharges.toFixed(2)) },
      );
    }
  };

  return (
    <>
      <CustomAppDialog open={PurchaseItemDeleteConfirmDialog}>
        <>
          <Typography.h5>{"Delete Purchase Item"}</Typography.h5>
          <GridLayout>
            <GridLayoutItem>
              <Typography.p>
                Are you sure you want to delete this purchase item?
              </Typography.p>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <CustomDialogCloseButton
                themeColor="error"
                closeDialogFunction={closePurchaseItemDeleteConfirmDialog}
              />
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={() => handleDeleteAction(PurchaseItemIndex)}
                type="button"
              >
                Delete
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </>
      </CustomAppDialog>

      <GridLayout cols={[{ width: "50%" }, { width: "50%" }]}>
        <GridLayoutItem>
          <Typography.h5 style={{ marginBottom: 5 }}>
            Purchase Items
          </Typography.h5>
        </GridLayoutItem>
        <GridLayoutItem style={{ textAlign: "left", marginLeft: "auto" }}>
          <Button
            fillMode={"solid"}
            themeColor={"primary"}
            onClick={pushElementInArray}
            type="button"
          >
            Add Item
          </Button>
        </GridLayoutItem>
      </GridLayout>

      {fieldArrayRenderProps &&
        fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 && (
          <div
            className="table-responsive"
            style={{
              maxHeight: "30vh",
              scrollbarWidth: "thin",
              marginTop: 10,
            }}
          >
            <table
              className="table table-striped table-bordered table-hover field-array-table"
              style={{ minWidth: 1200 }}
            >
              <thead>
                <tr>
                  <th style={{ width: "15%" }}>Item / Product</th>
                  <th style={{ width: "12%" }}>Weight (gm)</th>
                  <th style={{ width: "10%" }}>Stone Pieces</th>
                  <th style={{ width: "12%" }}>CT Weight (ct)</th>
                  <th style={{ width: "12%" }}>Charges (₹)</th>
                  <th style={{ width: "12%" }}>Customer Weight (gm)</th>
                  <th style={{ width: "12%" }}>Charges For Label (₹)</th>
                  <th
                    style={{
                      width: "5%",
                      position: "sticky",
                      right: 0,
                      zIndex: 10,
                    }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {fieldArrayRenderProps.value &&
                  fieldArrayRenderProps.value.length > 0 &&
                  fieldArrayRenderProps.value.map(
                    (item: any, index: number) => (
                      <tr key={index}>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.ItemID`}
                            isAddNew={true}
                            addNewLink="item/create"
                            dispatcherFunction={findAllActiveItem}
                            placeholder="Select Item"
                            component={FormSelectionField}
                            validator={requiredValidator}
                            options={ItemList?.map((item: any) => {
                              return {
                                value: item.id,
                                label: item.item_name,
                              };
                            })}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.Weight`}
                            placeholder="0.000"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            onChange={() => {
                              handleChargesCalculation(index, "Weight");
                            }}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.StonePcs`}
                            placeholder="0"
                            type="number"
                            format="n0"
                            component={FormNumericTextField}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.CTWeight`}
                            placeholder="0.000"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            onChange={() => {
                              handleChargesCalculation(index, "CTWeight");
                            }}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.Charges`}
                            placeholder="0.00"
                            type="number"
                            format="n2"
                            component={FormNumericTextField}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.CustomeWeight`}
                            placeholder="0.000"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.ChargesForLable`}
                            placeholder="0.00"
                            type="number"
                            format="n2"
                            component={FormNumericTextField}
                          />
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                            backgroundColor: "#fff",
                            textAlign: "center",
                          }}
                        >
                          <IconButton
                            type="button"
                            fillMode="solid"
                            size="small"
                            title="Delete"
                            themeColor="error"
                            onClick={() => handleOpenDeleteDialog(index)}
                            style={{
                              height: 32,
                              width: 32,
                            }}
                          >
                            <MdDelete
                              className="absolute-position"
                              style={{ fontSize: "22px" }}
                            />
                          </IconButton>
                        </td>
                      </tr>
                    ),
                  )}
              </tbody>
            </table>
          </div>
        )}

      {(!fieldArrayRenderProps.value ||
        fieldArrayRenderProps.value.length === 0) && (
        <div style={{ textAlign: "center", padding: "20px", color: "#6b7280" }}>
          <Typography.p>
            No items added. Click "Add Item" to add purchase items.
          </Typography.p>
        </div>
      )}
    </>
  );
};

export default PurchaseItemArray;
